import type { ProviderName } from '../lib/providerRouting';
import { isProviderName } from '../lib/providerRouting';

interface ApiErrorPayload {
  detail?: string;
}

export interface SymbolSearchResult {
  symbol: string;
  ticker: string;
  fullName: string;
  description: string;
  exchange: string | null;
  exchangeDisplay: string | null;
  type: string | null;
  category: string;
  provider: ProviderName;
  popularityScore: number | null;
}

export interface SymbolSearchResponse {
  symbols: SymbolSearchResult[];
  catalogWarming: boolean;
  nextOffset: number | null;
}

export interface FetchSymbolsOptions {
  offset?: number;
  limit?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readStringField(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

function readNullableStringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

function readNullableNumberField(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readProviderField(record: Record<string, unknown>, key: string): ProviderName {
  const value = record[key];
  if (isProviderName(value)) {
    return value;
  }
  throw new Error('Symbol search returned a result without a valid provider.');
}

function parseSymbolSearchResult(item: unknown): SymbolSearchResult | null {
  if (typeof item === 'string') {
    throw new Error('Symbol search returned a legacy providerless result. Refresh the page and try again.');
  }

  if (!isRecord(item)) {
    return null;
  }

  const symbol = readStringField(item, 'symbol').trim();
  if (symbol.length === 0) {
    return null;
  }

  const ticker = readStringField(item, 'ticker').trim();
  const fullName = readStringField(item, 'fullName').trim();
  const description = readStringField(item, 'description').trim();

  return {
    symbol,
    ticker: ticker.length > 0 ? ticker : symbol,
    fullName: fullName.length > 0 ? fullName : symbol,
    description: description.length > 0 ? description : symbol,
    exchange: readNullableStringField(item, 'exchange'),
    exchangeDisplay: readNullableStringField(item, 'exchangeDisplay'),
    type: readNullableStringField(item, 'type'),
    category: readStringField(item, 'category').trim() || 'All',
    provider: readProviderField(item, 'provider'),
    popularityScore: readNullableNumberField(item, 'popularityScore'),
  };
}

async function parseApiError(response: Response): Promise<Error> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const payload = (await response.json()) as ApiErrorPayload;
    if (typeof payload.detail === 'string' && payload.detail.length > 0) {
      return new Error(payload.detail);
    }
  }
  return new Error(`Request failed with status ${response.status}`);
}

function parseBooleanField(record: Record<string, unknown>, key: string): boolean {
  return record[key] === true;
}

function parseNullableIntegerField(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    return null;
  }
  return value;
}

function parseSymbolList(payload: unknown): SymbolSearchResponse {
  if (Array.isArray(payload)) {
    return {
      symbols: payload
        .map((item) => parseSymbolSearchResult(item))
        .filter((item): item is SymbolSearchResult => item !== null),
      catalogWarming: false,
      nextOffset: null,
    };
  }

  if (!isRecord(payload)) {
    return { symbols: [], catalogWarming: false, nextOffset: null };
  }

  if (!Array.isArray(payload.symbols)) {
    return { symbols: [], catalogWarming: false, nextOffset: null };
  }

  return {
    symbols: payload.symbols
      .map((item) => parseSymbolSearchResult(item))
      .filter((item): item is SymbolSearchResult => item !== null),
    catalogWarming: parseBooleanField(payload, 'catalogWarming'),
    nextOffset: parseNullableIntegerField(payload, 'nextOffset'),
  };
}

export async function fetchSymbols(query: string, category = 'All', options: FetchSymbolsOptions = {}): Promise<SymbolSearchResponse> {
  const params = new URLSearchParams();
  const trimmedQuery = query.trim();
  if (trimmedQuery.length > 0) {
    params.set('q', trimmedQuery);
  }
  params.set('category', category);
  if (typeof options.offset === 'number' && Number.isInteger(options.offset) && options.offset > 0) {
    params.set('offset', String(options.offset));
  }
  if (typeof options.limit === 'number' && Number.isInteger(options.limit) && options.limit > 0) {
    params.set('limit', String(options.limit));
  }

  const queryString = params.toString();
  const response = await fetch(
    queryString.length > 0 ? `/api/symbols/search?${queryString}` : '/api/symbols/search',
    {
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw await parseApiError(response);
  }

  const payload = (await response.json()) as unknown;
  return parseSymbolList(payload);
}
